// v2 mangrove — the tree stands on a real basket of arching stilt roots: each
// prop root springs from the trunk collar, bows outward over a knee and plunges
// into the mud in two chained segments, with thin young drop-roots hanging
// straight down off the boughs — under a short trunk that forks into two leaning
// stems and a layered, spreading canopy of flattened tufts. Green propagule pods
// dangle below the leaf line. Variants re-deal the root basket, the fork yaws and
// the canopy. Ported 1:1 from age-of-mcp's v2/mangrove.ts, using the library's
// shared `tropic` (h01, lerp, tube, tuft) helpers.
import * as THREE from "three";
import { defineModel, tropic, type Part, type Vec3 } from "../../lib";

const BARK = "#63503a";
const ROOT = "#52402c";
const ROOT_DK = "#443420";
const LEAF = "#34693c";
const LEAF_2 = "#3f7d45";
const LEAF_DK = "#2b5a33";
const POD = "#6a9b42";

function mangroveParts(v: number): Part[] {
  const seed = v * 41 + 21;
  const yaw = v * 1.8 + tropic.h01(seed, 0) * 0.7;
  const collar: Vec3 = [0, 0.52, 0];
  const parts: Part[] = [];
  // The stilt-root basket: each prop root bows out over a knee and
  // plunges into the mud.
  for (let i = 0; i < 6; i++) {
    const a = yaw + (i / 6) * Math.PI * 2 + tropic.h01(seed, 10 + i) * 0.5;
    const kr = tropic.lerp(0.2, 0.26, tropic.h01(seed, 20 + i));
    const fr = tropic.lerp(0.38, 0.5, tropic.h01(seed, 30 + i));
    const knee: Vec3 = [Math.cos(a) * kr,
      tropic.lerp(0.28, 0.36, tropic.h01(seed, 40 + i)), Math.sin(a) * kr];
    const c = i % 2 ? ROOT : ROOT_DK;
    parts.push(tropic.tube(collar, knee, 0.045, 0.032, c, 5));
    parts.push(tropic.tube(knee, [Math.cos(a) * fr, 0, Math.sin(a) * fr],
      0.032, 0.016, c, 4));
  }
  // Short trunk rising off the basket, forking into two leaning stems.
  const forkP: Vec3 = [0.03, 0.88, -0.02];
  parts.push(tropic.tube(collar, forkP, 0.085, 0.065, BARK, 6));
  parts.push(tropic.tube(forkP,
    [Math.cos(yaw) * 0.24, 1.18, Math.sin(yaw) * 0.24], 0.055, 0.03, BARK, 5));
  parts.push(tropic.tube(forkP,
    [Math.cos(yaw + 2.4) * 0.2, 1.12, Math.sin(yaw + 2.4) * 0.2],
    0.05, 0.026, BARK, 5));
  // Thin young drop-roots hanging off the boughs down into the mud.
  for (let i = 0; i < 2; i++) {
    const a = yaw + 1.1 + i * 2.8 + tropic.h01(seed, 50 + i) * 0.5;
    const dx = Math.cos(a) * 0.2, dz = Math.sin(a) * 0.2;
    parts.push(tropic.tube([dx, 1.05, dz], [dx * 1.3, 0, dz * 1.3],
      0.011, 0.005, ROOT_DK, 4));
  }
  // Layered spreading canopy: a ragged ring of flattened tufts under a
  // top cap.
  for (let i = 0; i < 5; i++) {
    const a = yaw + 0.4 + (i / 5) * Math.PI * 2 + tropic.h01(seed, 60 + i) * 0.4;
    const r = tropic.lerp(0.24, 0.3, tropic.h01(seed, 70 + i));
    parts.push(tropic.tuft(Math.cos(a) * 0.42,
      tropic.lerp(1.16, 1.24, tropic.h01(seed, 80 + i)), Math.sin(a) * 0.42,
      r, 0.5, i % 2 ? LEAF : LEAF_DK, a));
  }
  parts.push(tropic.tuft(0.02, 1.34, 0, 0.34, 0.52, LEAF_2, yaw + 0.6));
  // Propagule pods dangling below the leaf line.
  for (let i = 0; i < 3; i++) {
    const a = yaw + 0.9 + i * 2.1 + tropic.h01(seed, 90 + i) * 0.5;
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.05, 0),
      color: POD,
      position: [Math.cos(a) * 0.32, 1.0, Math.sin(a) * 0.32],
      scale: [0.32, 1.7, 0.32] });
  }
  return parts;
}

export const mangroveV2 = defineModel({
  id: "mangrove-v2",
  batchId: "fable",
  name: "Mangrove (v2)",
  tags: ["scenery","mangrove","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly mangrove.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.6,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => mangroveParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => mangroveParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => mangroveParts(2) },
  ],
  animations: [],
});
